import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Rocket, Palette, Layout as LayoutIcon } from "lucide-react";
import { applyTheme } from "@/components/SiteTheme";

const FONTS = ["Outfit", "DM Sans", "Inter", "Poppins", "Montserrat", "Lora", "Playfair Display", "Roboto", "Nunito"];

function useSettings(key) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let ignore = false;
    api.get(`/admin/cms/settings/${key}`).then(({ data }) => { if (!ignore) setDraft((cur) => (cur == null ? data.draft : cur)); }).catch((e) => toast.error(formatApiError(e)));
    return () => { ignore = true; };
  }, [key]);
  const save = async (publish) => {
    setSaving(true);
    try {
      await api.put(`/admin/cms/settings/${key}`, { values: draft });
      if (publish) await api.post(`/admin/cms/settings/${key}/publish`);
      toast.success(publish ? "Publié ✅ — appliqué à tout le site" : "Brouillon enregistré");
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  return { draft, setDraft, saving, save };
}

export function ThemeTab() {
  const { draft, setDraft, saving, save } = useSettings("theme");
  const livePreview = useRef(false);
  // Aperçu en direct : applique le thème au site pendant l'édition
  useEffect(() => { if (draft && livePreview.current) applyTheme(draft); }, [draft]);
  if (!draft) return <p className="text-slate-400">Chargement…</p>;
  const set = (patch) => { livePreview.current = true; setDraft((d) => ({ ...d, ...patch })); };
  return (
    <div className="max-w-xl" data-testid="cms-theme-tab">
      <div className="flex items-center gap-2 mb-1 text-slate-800 font-semibold"><Palette size={18} className="text-[#E05638]" /> Styles globaux</div>
      <p className="text-sm text-slate-500 mb-5">La couleur et les polices s'appliquent à <strong>tout le site</strong>. L'aperçu est immédiat ; cliquez sur Publier pour rendre les changements définitifs pour les visiteurs.</p>
      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <Label className="text-sm font-semibold">Couleur principale</Label>
          <div className="flex items-center gap-2 mt-2">
            <input type="color" value={draft.primary || "#E05638"} onChange={(e) => set({ primary: e.target.value })} className="h-10 w-16 rounded border border-slate-200 cursor-pointer" data-testid="theme-primary" />
            <Input value={draft.primary || ""} onChange={(e) => set({ primary: e.target.value })} className="h-10 w-40" />
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-4 grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-sm font-semibold">Police des titres</Label>
            <Select value={draft.headingFont || "Outfit"} onValueChange={(v) => set({ headingFont: v })}>
              <SelectTrigger className="h-10 mt-1" data-testid="theme-heading-font"><SelectValue /></SelectTrigger>
              <SelectContent>{FONTS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-semibold">Police du texte</Label>
            <Select value={draft.bodyFont || "DM Sans"} onValueChange={(v) => set({ bodyFont: v })}>
              <SelectTrigger className="h-10 mt-1" data-testid="theme-body-font"><SelectValue /></SelectTrigger>
              <SelectContent>{FONTS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <div className="mt-5 flex gap-2">
        <Button variant="outline" disabled={saving} onClick={() => save(false)} data-testid="theme-save-draft"><Save size={15} className="mr-1.5" /> Brouillon</Button>
        <Button disabled={saving} onClick={() => save(true)} className="bg-[#E05638] text-white" data-testid="theme-publish"><Rocket size={15} className="mr-1.5" /> Publier sur tout le site</Button>
      </div>
    </div>
  );
}

export function LayoutTab() {
  const { draft, setDraft, saving, save } = useSettings("layout");
  if (!draft) return <p className="text-slate-400">Chargement…</p>;
  const banner = draft.banner || {};
  const setBanner = (patch) => setDraft((d) => ({ ...d, banner: { ...(d.banner || {}), ...patch } }));
  return (
    <div className="max-w-xl" data-testid="cms-layout-tab">
      <div className="flex items-center gap-2 mb-1 text-slate-800 font-semibold"><LayoutIcon size={18} className="text-[#E05638]" /> En-tête & pied de page</div>
      <p className="text-sm text-slate-500 mb-5">L'en-tête et le pied de page actuels sont conservés. Vous ajoutez ici un <strong>bandeau d'annonce</strong> (tout en haut) et une <strong>note de pied de page</strong>, répercutés sur tout le site.</p>
      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-800">Bandeau d'annonce (en haut du site)</div>
            <Switch checked={!!banner.enabled} onCheckedChange={(v) => setBanner({ enabled: v })} data-testid="layout-banner-enabled" />
          </div>
          <Input value={banner.text || ""} onChange={(e) => setBanner({ text: e.target.value })} placeholder="Texte du bandeau (ex. Offre de lancement…)" className="h-10" data-testid="layout-banner-text" />
          <Input value={banner.href || ""} onChange={(e) => setBanner({ href: e.target.value })} placeholder="Lien au clic (facultatif : /devenir-partenaire ou https://…)" className="h-10" data-testid="layout-banner-href" />
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2"><Label className="text-xs">Fond</Label><input type="color" value={banner.bg || "#172A3A"} onChange={(e) => setBanner({ bg: e.target.value })} className="h-9 w-12 rounded border border-slate-200 cursor-pointer" data-testid="layout-banner-bg" /></div>
            <div className="flex items-center gap-2"><Label className="text-xs">Texte</Label><input type="color" value={banner.color || "#ffffff"} onChange={(e) => setBanner({ color: e.target.value })} className="h-9 w-12 rounded border border-slate-200 cursor-pointer" data-testid="layout-banner-color" /></div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <Label className="text-sm font-semibold">Note de pied de page</Label>
          <Textarea rows={3} className="mt-2" value={draft.footer_note || ""} onChange={(e) => setDraft((d) => ({ ...d, footer_note: e.target.value }))} placeholder="Texte additionnel affiché en bas de toutes les pages (facultatif)" data-testid="layout-footer-note" />
        </div>
      </div>
      <div className="mt-5 flex gap-2">
        <Button variant="outline" disabled={saving} onClick={() => save(false)} data-testid="layout-save-draft"><Save size={15} className="mr-1.5" /> Brouillon</Button>
        <Button disabled={saving} onClick={() => save(true)} className="bg-[#E05638] text-white" data-testid="layout-publish"><Rocket size={15} className="mr-1.5" /> Publier</Button>
      </div>
    </div>
  );
}
